import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import { emailClienteService } from '../../services/emailClienteService';
import { verificarDadosEmailCliente } from '../../services/siCloudFunctionsService';
import { camposAberturaDosModelos, emailValido, modelosAplicaveis, variaveisUsadas } from '../../models/emailCliente.modelos';
import type { DadosClienteSolicitacao, ModeloEmailClienteSalvar } from '../../models/emailCliente.modelos';
import type { TipoSolicitacao } from '../../models';

const CHAVE = ['si', 'config', 'emails-cliente'] as const;

/** Modelos de e-mail para o Cliente — lidos também na abertura (bloco "Dados para o cliente"). */
export function useModelosEmailClienteQuery() {
  return useQuery({
    queryKey: CHAVE,
    queryFn: () => emailClienteService.listar(),
    staleTime: 5 * 60 * 1000,
  });
}

/** Campos do bloco "Dados para o cliente" pra esse tipo/Clientes — vazio quando nenhum
 * modelo de e-mail se aplica (o bloco nem aparece). Pede também o RG quando o modelo usa e o
 * Digital não tem (verificarDadosEmailCliente): no perfil do colaborador ou, na Admissão, no
 * pré-cadastro que a pessoa preencheu (pelo e-mail pessoal informado). */
export function useCamposAberturaEmailCliente(
  tipo: TipoSolicitacao | '',
  clienteIds: Array<string | null | undefined>,
  pessoa: { colaboradorId?: string | null; emailPessoal?: string | null }
) {
  const { data: modelos = [] } = useModelosEmailClienteQuery();
  const chaveClientes = clienteIds.filter(Boolean).join(',');
  const aplicaveis = useMemo(() => modelosAplicaveis(modelos, tipo, chaveClientes.split(',')), [modelos, tipo, chaveClientes]);
  const usaRg = variaveisUsadas(aplicaveis.flatMap((m) => [m.assunto, m.corpo])).has('rg');
  const email = pessoa.emailPessoal?.trim().toLowerCase() ?? '';
  const consulta =
    tipo === 'admissao'
      ? emailValido(email)
        ? { emailPessoal: email }
        : null
      : pessoa.colaboradorId
        ? { colaboradorId: pessoa.colaboradorId }
        : null;
  const { data: dadosCadastro } = useQuery({
    queryKey: ['si', 'dados-email-cliente', consulta],
    queryFn: () => verificarDadosEmailCliente(consulta!),
    enabled: usaRg && consulta !== null,
    staleTime: 5 * 60 * 1000,
  });
  // Admissão sem e-mail válido ainda: pede (não tem como saber).
  const rgFaltando =
    usaRg && ((tipo === 'admissao' && consulta === null) || dadosCadastro?.rgCadastrado === false);
  return useMemo(
    () => camposAberturaDosModelos(aplicaveis, new Set(rgFaltando ? ['rg'] : [])),
    [aplicaveis, rgFaltando]
  );
}

function useAtor() {
  const { user } = useAuth();
  return {
    id: user?.collaboratorId ?? user?.uid ?? '',
    name: user?.displayName ?? user?.email ?? '',
    avatar: user?.photoURL ?? null,
  };
}

export function useModeloEmailClienteMutations() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const por = useAtor();
  const invalidar = () => queryClient.invalidateQueries({ queryKey: CHAVE });
  return {
    salvar: useMutation({
      mutationFn: (modelo: ModeloEmailClienteSalvar) => emailClienteService.salvar(modelo, por),
      onSuccess: () => {
        toast.success('Modelo de e-mail salvo.');
        invalidar();
      },
      onError: () => toast.error('Não foi possível salvar o modelo. Tente de novo.'),
    }),
    excluir: useMutation({
      mutationFn: (id: string) => emailClienteService.excluir(id),
      onSuccess: () => {
        toast.success('Modelo de e-mail excluído.');
        invalidar();
      },
      onError: () => toast.error('Não foi possível excluir. Tente de novo.'),
    }),
  };
}

export function useSalvarDadosClienteMutation(solicitacaoId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const por = useAtor();
  return useMutation({
    mutationFn: (dados: DadosClienteSolicitacao) => emailClienteService.salvarDadosCliente(solicitacaoId, dados, por),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['si', 'solicitacoes'] }),
    onError: () => toast.error('Não foi possível salvar os dados para o cliente.'),
  });
}
